import { clsx } from "clsx";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import type { StageTreeNode } from "../../types.ts";

interface StageComboboxProps {
	stages: StageTreeNode[];
	value: string;
	onChange: (next: string) => void;
	excludeIds?: Set<string>;
	id?: string;
	disabled?: boolean;
	allowNone?: boolean;
	placeholder?: string;
	noneLabel?: string;
	modal?: boolean;
	// Mirrors the trigger's visible text as its accessible name; leave unset
	// when an external <label htmlFor> already names the combobox.
	labelFromValue?: boolean;
}

function normalize(s: string) {
	return s
		.toLowerCase()
		.normalize("NFD")
		.replace(/\p{Diacritic}/gu, "");
}

export function StageCombobox({
	stages,
	value,
	onChange,
	excludeIds,
	id,
	disabled,
	allowNone = true,
	placeholder = "Nenhuma (etapa raiz)",
	noneLabel = "Nenhuma (etapa raiz)",
	modal,
	labelFromValue,
}: StageComboboxProps) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");

	const candidates = useMemo(
		() => stages.filter((stage) => !excludeIds?.has(stage.id)),
		[stages, excludeIds],
	);

	const filtered = useMemo(() => {
		const q = normalize(query.trim());
		if (!q) return candidates;
		return candidates.filter((stage) => normalize(stage.name).includes(q));
	}, [candidates, query]);

	const selected = candidates.find((stage) => stage.id === value);
	const indentByDepth = !query.trim();

	function pick(next: string) {
		onChange(next);
		setOpen(false);
		setQuery("");
	}

	return (
		<Popover open={open} onOpenChange={setOpen} modal={modal}>
			<PopoverTrigger asChild>
				<Button
					id={id}
					type="button"
					variant="outline"
					role="combobox"
					aria-expanded={open}
					aria-label={
						labelFromValue ? (selected?.name ?? placeholder) : undefined
					}
					disabled={disabled}
					className="stage-combobox__trigger"
				>
					<span
						className={clsx(
							"stage-combobox__trigger-text",
							!selected && "is-empty",
						)}
					>
						{selected ? selected.name : placeholder}
					</span>
					<ChevronsUpDown className="stage-combobox__chevron" />
				</Button>
			</PopoverTrigger>
			<PopoverContent className="stage-combobox__content" align="start">
				<div className="stage-combobox__search">
					<div className="stage-combobox__search-field">
						<Search className="stage-combobox__search-icon" />
						<Input
							autoFocus
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder="Buscar etapa..."
							className="stage-combobox__search-input"
						/>
					</div>
				</div>
				<div className="stage-combobox__list">
					{allowNone && (
						<button
							type="button"
							onClick={() => pick("")}
							className={clsx("stage-combobox__option", !value && "is-active")}
						>
							<span className="stage-combobox__none-label">{noneLabel}</span>
							{!value && <Check />}
						</button>
					)}
					{filtered.length === 0 ? (
						<p className="stage-combobox__empty">Nenhuma etapa encontrada.</p>
					) : (
						filtered.map((stage) => {
							const active = stage.id === value;
							return (
								<button
									key={stage.id}
									type="button"
									onClick={() => pick(stage.id)}
									className={clsx(
										"stage-combobox__item",
										active && "is-active",
									)}
									style={
										indentByDepth
											? { paddingLeft: `${0.5 + stage.depth}rem` }
											: undefined
									}
								>
									<span className="stage-combobox__item-name">
										{stage.name}
									</span>
									{active && <Check />}
								</button>
							);
						})
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
